import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FiMapPin, FiGrid } from 'react-icons/fi';
import { motion } from 'framer-motion';
import backgroundImage from '../../../assets/images/angel.jpg';
import {
  fetchStates,
  fetchCities,
  fetchSpecialties,
  selectStates,
  selectCities,
  selectSpecialties,
  selectSpecialtyById,
  clearCities,
} from '../../../redux/slices/dataSlice';

const BuscaAbogadoSection = () => {
  const dispatch = useDispatch();
  const states = useSelector(selectStates);
  const cities = useSelector(selectCities);
  const specialties = useSelector(selectSpecialties);

  const [formData, setFormData] = useState({
    category: '',
    estado: '',
    ciudad: '',
  });

  const selectedSpecialty = useSelector(state => selectSpecialtyById(state, formData.category));

  // Fetch initial data
  useEffect(() => {
    // Fetch states and specialties on component mount
    dispatch(fetchStates(142)); // 142 is Mexico's country ID
    dispatch(fetchSpecialties());
  }, [dispatch]);

  const handleInputChange = field => e => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'estado' ? { ciudad: '' } : {}), // reset ciudad si cambia estado
    }));

    // When estado changes, fetch cities for that state
    if (field === 'estado' && value) {
      dispatch(clearCities());
      const selectedState = states.data?.find(state => state.name === value);
      if (selectedState) {
        dispatch(fetchCities(selectedState.id));
      }
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    console.log('Búsqueda:', formData);
    // Aquí iría la lógica de búsqueda
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const headerFrameVariants = {
    hidden: {
      opacity: 0,
      x: -100,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1],
        delay: 0.2,
      },
    },
  };

  return (
    <>
      {/* Header fuera del fondo */}
      <div>
        <motion.div
          className="bg-custom-blue text-white p-4 flex items-center gap-4 overflow-hidden"
          variants={headerFrameVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Title */}
          <h2 className="text-3xl font-bold font-serif whitespace-nowrap ml-4 py-2">
            Busca un Abogado
          </h2>
        </motion.div>
      </div>

      <section
        className="relative py-10 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        {/* Overlay negro con baja opacidad */}
        <div className="absolute inset-0 bg-black bg-opacity-80"></div>

        <div className="relative max-w-lg mx-auto px-8">
          {/* Descripción */}
          <motion.div
            className="mb-12"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <p className="text-xl md:text-2xl leading-relaxed py-2 text-white">
              Accede a nuestro listado de expertos cuidadosamente seleccionados, para encontrar a tu
              abogado ideal.
            </p>
          </motion.div>

          {/* Search Form */}
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-md p-8 my-16 mx-auto max-w-md"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2, margin: '0px 0px -100px 0px' }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Especialidad */}
              <motion.div variants={itemVariants}>
                <div className="relative">
                  <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-blue-500">
                    <FiGrid className="w-5 h-5" />
                  </div>
                  <select
                    value={formData.category}
                    onChange={handleInputChange('category')}
                    className="w-full pl-14 pr-12 py-5 bg-white border-0 rounded-xl focus:outline-none text-gray-800 text-base appearance-none cursor-pointer shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <option value="">Especialidad</option>
                    {specialties.loading ? (
                      <option disabled>Cargando especialidades...</option>
                    ) : (
                      specialties.data?.map(specialty => (
                        <option key={specialty.id} value={specialty.id}>
                          {specialty.name}
                        </option>
                      )) || <option disabled>No hay especialidades disponibles</option>
                    )}
                  </select>
                  <div className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {/* Descripción de la especialidad */}
                {selectedSpecialty && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="mt-3 px-4 py-3 bg-blue-50 rounded-lg border-l-4 border-blue-500"
                  >
                    <p className="text-sm text-blue-800 leading-relaxed">
                      <strong>{selectedSpecialty.name}:</strong> {selectedSpecialty.description}
                    </p>
                  </motion.div>
                )}
              </motion.div>

              {/* Estado */}
              <motion.div variants={itemVariants}>
                <div className="relative">
                  <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-blue-500">
                    <FiMapPin className="w-5 h-5" />
                  </div>
                  <select
                    value={formData.estado}
                    onChange={handleInputChange('estado')}
                    className="w-full pl-14 pr-12 py-5 bg-white border-0 rounded-xl focus:outline-none text-gray-800 text-base appearance-none cursor-pointer shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <option value="">Estado</option>
                    {states.loading ? (
                      <option disabled>Cargando estados...</option>
                    ) : (
                      states.data?.map(estado => (
                        <option key={estado.id} value={estado.name}>
                          {estado.name}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </motion.div>

              {/* Ciudad */}
              <motion.div variants={itemVariants}>
                <div className="relative">
                  <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-blue-500">
                    <FiMapPin className="w-5 h-5" />
                  </div>
                  <select
                    value={formData.ciudad}
                    onChange={handleInputChange('ciudad')}
                    disabled={!formData.estado}
                    className="w-full pl-14 pr-12 py-5 bg-white border-0 rounded-xl focus:outline-none text-gray-800 text-base appearance-none cursor-pointer shadow-sm hover:shadow-md transition-all duration-200 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">
                      {formData.estado ? 'Ciudad' : 'Ciudad (Selecciona un estado primero)'}
                    </option>
                    {cities.loading ? (
                      <option disabled>Cargando ciudades...</option>
                    ) : (
                      cities.data?.map(ciudad => (
                        <option key={ciudad.id} value={ciudad.name}>
                          {ciudad.name}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.div variants={itemVariants} className="pt-4">
                <button
                  type="submit"
                  className="w-3/4 bg-blue-600 text-white py-3 rounded-full font-medium text-base hover:bg-blue-700 transition-colors duration-200 focus:outline-none shadow-sm hover:shadow-lg items-center flex justify-center mx-auto"
                >
                  Buscar abogado
                </button>
              </motion.div>
            </form>
          </motion.div>

          {/* Lo más buscado Section */}
          <motion.div
            variants={itemVariants}
            className="space-y-8 py-6 mt-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h4
              className="text-3xl md:text-2xl font-bold text-gray-200"
              variants={itemVariants}
            >
              Lo más buscado...
            </motion.h4>

            <div className="space-y-8">
              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                className="transition-transform duration-200"
              >
                <h5 className="font-bold text-gray-200 mb-3 text-lg">Derecho Familiar</h5>
                <p className="text-base text-gray-100 leading-relaxed text-justify">
                  Conéctate con abogados expertos en matrimonios, divorcios, adopciones y más, en un
                  solo clic.
                </p>
              </motion.div>

              <motion.hr
                className="border-gray-300"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                style={{ transformOrigin: 'left' }}
              />

              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                className="transition-transform duration-200"
              >
                <h5 className="font-bold text-gray-200 mb-3 text-lg">Derecho Penal</h5>
                <p className="text-base text-gray-100 leading-relaxed text-justify">
                  Consigue un abogado especialista en asesoría para víctimas y acusados de un
                  delito.
                </p>
              </motion.div>

              <motion.hr
                className="border-gray-300"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{ transformOrigin: 'left' }}
              />

              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                className="transition-transform duration-200"
              >
                <h5 className="font-bold text-gray-200 mb-3 text-lg">Derecho Laboral</h5>
                <p className="text-base text-gray-100 leading-relaxed text-justify">
                  Encuentra las mejores soluciones para tu trabajo o empresa de la mano de nuestros
                  abogados especialistas en materia laboral.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default BuscaAbogadoSection;
